import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useState, Activity, useEffect } from 'react';

import {
  IconEye,
  IconBook,
  IconBook2,
  IconTrash,
  IconXboxX,
  IconSearch,
  IconMoodKid,
  IconLockOpen,
  IconAdjustments,
  IconInfoTriangle,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { SchoolStatusConfig } from '@/constants/constant';
import { ESchoolStatus, ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  softdeleteRecord,
  getSchoolDropdown,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import { getTeacherList, getTeacherSummary } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import {
  ITeacherData,
  ISelectedRowValue,
  ITeacherSummaryData,
  TTeacherSummaryItem,
  ITeacherFilterValues,
} from '@/types';
import { getAssetUrl, formatLastActive, getStartOfMonthUnix } from '@/utils';

import TeacherDetailsModal from './modals/TeacherDetailsModal';
import TeacherListFilterModal from './modals/TeacherListFilterModal';

const TEACHER_SUMMARY_CONFIG = [
  {
    key: 'total_teachers',
    icon: <IconBook2 color="#717784" />,
    label: 'teacher.summary-cards.total-teacher',
  },
  {
    key: 'total_lessons_created',
    icon: <IconBook color="#717784" />,
    label: 'teacher.summary-cards.total-lesson',
  },
  {
    key: 'total_assigned_students',
    icon: <IconMoodKid color="#717784" />,
    label: 'teacher.summary-cards.total-assigned-students',
  },
  {
    key: 'inactive_teachers',
    icon: <IconInfoTriangle color="#717784" />,
    label: 'teacher.summary-cards.inactive-teacher',
  },
];

const Teacher = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [isTeacherDetailsOpen, setIsTeacherDetailsOpen] = useState(false);
  const [isRemoveTeacherModal, setIsRemoveTeacherModal] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<ITeacherFilterValues>({
    school_id: [],
    login_days: '',
    status: '',
  });

  const [teachers, setTeachers] = useState<ITeacherData[]>([]);
  const {
    loadingTable,
    setLoadingTable,
    loadingSummary,
    setLoadingSummary,
    loadingModal,
    setLoadingModal,
  } = useCommonStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [statsData, setStatsData] = useState<TTeacherSummaryItem[]>(
    TEACHER_SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
      trend: '0%',
      trendLabel: 'common.this-month',
      trendDirection: 'STABLE',
    }))
  );
  const [selectedTeacher, setSelectedTeacher] =
    useState<ISelectedRowValue | null>(null);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [schoolOptions, setSchoolOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { formatText } = useFormatText();
  const pageTitle = formatText('teacher.title');
  const userManagement = formatText('user-management.title');

  useEffect(() => {
    const fetchSchools = async () => {
      const response = await getSchoolDropdown();
      if (response?.status) {
        const options =
          response?.data?.map((item: { id: string; name: string }) => ({
            value: item?.id,
            label: item?.name,
          })) || [];
        setSchoolOptions(options);
      }
    };
    fetchSchools();
  }, []);

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, userManagement]);

  const fetchTeachers = async () => {
    setLoadingTable(true);
    const params = {
      search: debouncedSearch || undefined,
      school_id: filterValues?.school_id,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      order: sortColumn ? { [sortColumn]: sortDirection } : undefined,
      status: filterValues?.status || undefined,
      login_days: filterValues?.login_days || undefined,
    };
    const response = await getTeacherList(params);
    if (response?.status) {
      setTeachers(response?.data || []);
      setTotal(response?.total || 0);
    }
    setLoadingTable(false);
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    const monthTimestamp = getStartOfMonthUnix();

    const response = await getTeacherSummary({
      month: monthTimestamp,
    });

    if (response?.status) {
      const apiData = response?.data as ITeacherSummaryData;
      const mappedSummary = TEACHER_SUMMARY_CONFIG?.map((item) => {
        const data = apiData[item?.key as keyof ITeacherSummaryData];
        return {
          ...item,
          value: (data?.total || 0)?.toLocaleString(),
          trend: `${data?.percentage || 0}%`,
          trendLabel: 'common.this-month',
          trendDirection: (data?.trend || 'stable')?.toUpperCase(),
          isPositive: data?.is_positive,
        };
      });
      setStatsData(mappedSummary);
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterValues]);

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    filterValues,
    currentPage,
    pageSize,
    sortColumn,
    sortDirection,
  ]);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'teacher.columns.name',
      key: 'name',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('teacher_name'),
      render: (row: ITeacherData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row?.id,
                name: row?.teacher_name || '-',
                image: getAssetUrl(row?.asset) || row?.user_img || '',
              },
            ]}
            size={40}
            avatarClassName="w-8 h-8 text-[10px]"
          />
          <span className="font-medium text-neutral-primary">
            {row?.teacher_name || '-'}
          </span>
        </div>
      ),
    },
    {
      title: 'teacher.columns.email',
      key: 'email',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('email'),
    },
    {
      title: 'teacher.columns.school',
      key: 'school',
      width: '15%',
      sorter: true,
      render: (row: ITeacherData) => row?.school_details?.name || '-',
      handleSort: () => handleSort('school'),
    },
    {
      title: 'teacher.columns.lesson-created',
      key: 'total_lessons',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('total_lessons'),
      render: (row: ITeacherData) => (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-semibold bg-pink-light-50 text-pink-secondary'
          )}
        >
          {row?.total_lessons ?? 0}
        </span>
      ),
    },
    {
      title: 'teacher.columns.assigned-class',
      key: 'total_assigned_classes',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('total_assigned_classes'),
      render: (row: ITeacherData) => (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-semibold bg-warning-light-100 text-warning-secondary'
          )}
        >
          {row?.total_assigned_classes ?? 0}
        </span>
      ),
    },
    {
      title: 'teacher.columns.status',
      key: 'status',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: ITeacherData) => {
        const config =
          SchoolStatusConfig[row?.status as keyof typeof SchoolStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'teacher.columns.last_active',
      key: 'last_active',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('last_active'),
      render: (row: ITeacherData) =>
        formatLastActive(row?.last_active, formatText),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '5%',
      align: 'center',
      render: (row: ITeacherData) => {
        const isBlocked = row.status === ESchoolStatus.IN_ACTIVE;
        const teacher = {
          name: row.teacher_name,
          id: row.id,
          status: row.status,
        };

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedTeacher({ name: teacher.name, id: teacher.id });
              setIsTeacherDetailsOpen(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockTeacher(teacher);
              } else {
                setSelectedTeacher(teacher);
                setIsOpenBlockModal(true);
              }
            },
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => {
              setSelectedTeacher({ name: teacher.name, id: teacher.id });
              setIsRemoveTeacherModal(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const handleFilterApply = (filters: ITeacherFilterValues) => {
    setFilterValues(filters);
  };

  const handleBlockTeacher = async (teacher = selectedTeacher) => {
    if (!teacher) return;
    setLoadingModal(true);
    try {
      const params = {
        id: teacher.id,
        record_type: ERestoreEntityType.TEACHER,
        is_block: teacher.status === ESchoolStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchTeachers();
        fetchSummary();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setSelectedTeacher(null);
      setIsOpenBlockModal(false);
    }
  };

  const handleRemoveTeacher = async () => {
    if (!selectedTeacher) return;
    setLoadingModal(true);
    try {
      const params = {
        id: selectedTeacher.id.toString(),
        record_type: ERestoreEntityType.TEACHER,
      };
      const response = await softdeleteRecord(params);
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchTeachers();
        fetchSummary();
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setIsRemoveTeacherModal(false);
      setSelectedTeacher(null);
    }
  };

  const isFilterApplied =
    (filterValues?.school_id?.length ?? 0) > 0 ||
    !!filterValues?.login_days ||
    !!filterValues?.status;

  const showListUI =
    loadingTable ||
    teachers.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <div className="flex flex-wrap justify-start items-center gap-2.5 mb-6 pl-1">
          <Input
            name="search"
            placeholder={formatText('teacher.search-placeholder')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-77 md:min-w-77 w-full"
            inputClassName="py-[13px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 cursor-pointer',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
      )}

      {showListUI && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {statsData?.map((stat, index) => (
            <SummaryCard
              key={index}
              variant="dashboard"
              label={formatText(stat.label)}
              value={stat?.value}
              icon={stat?.icon}
              trendDirection={stat?.trendDirection}
              isPositive={stat?.isPositive}
              trendLabel={
                stat?.trendLabel ? formatText(stat.trendLabel) : undefined
              }
              trend={stat?.trend}
              isLoading={loadingSummary}
            />
          ))}
        </div>
      )}

      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={teachers}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isEmpty={
            !loadingTable &&
            teachers?.length === 0 &&
            search?.length === 0 &&
            debouncedSearch?.length === 0 &&
            !isFilterApplied
          }
          fieldName={sortColumn}
          sortDirection={sortDirection}
          emptyTitle={formatText('user-management.teacher.not-found-title')}
          emptyDescription={formatText(
            'user-management.teacher.not-found-desc'
          )}
          isEmptySearch={
            teachers?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          onRowClick={(id) => {
            const teacher = teachers.find((item) => item.id === id);
            if (teacher) {
              setSelectedTeacher({
                name: teacher.teacher_name,
                id: teacher.id,
                status: teacher.status,
              });
              setIsTeacherDetailsOpen(true);
            }
          }}
        />
      </div>

      <Activity mode={isTeacherDetailsOpen ? 'visible' : 'hidden'}>
        <TeacherDetailsModal
          setIsOpen={setIsTeacherDetailsOpen}
          selectedTeacherId={selectedTeacher?.id}
          onRemoveSubmit={fetchTeachers}
        />
      </Activity>
      <Activity mode={isRemoveTeacherModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveTeacherModal}
          setIsOpen={setIsRemoveTeacherModal}
          title="teacher.actions.remove-teacher-title"
          descriptionPrefix="teacher.actions.remove-teacher-prefix"
          descriptionSuffix="teacher.actions.remove-teacher-suffix"
          entityName={selectedTeacher?.name}
          confirmLabel={formatText('teacher.actions.remove-teacher')}
          onConfirm={handleRemoveTeacher}
          isLoading={loadingModal}
          isDestructive
        />
      </Activity>
      <Activity mode={isOpenBlockModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="teacher.actions.block-teacher-title"
          descriptionPrefix="teacher.actions.block-teacher-prefix"
          descriptionSuffix="teacher.actions.block-teacher-suffix"
          entityName={selectedTeacher?.name}
          confirmLabel={formatText('teacher.actions.block-teacher')}
          onConfirm={handleBlockTeacher}
          isLoading={loadingModal}
          isDestructive
        />
      </Activity>
      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <TeacherListFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
          schoolOptions={schoolOptions}
        />
      </Activity>
    </>
  );
};

export default Teacher;
